import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Store, ArrowRight, ExternalLink, Sparkles, CheckCircle2 } from 'lucide-react';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { getApiOauthProviderAuthorize } from '@/api/generated/oauth/oauth';
import { toast } from 'sonner';

interface ConnectStoreModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStoreConnected?: () => void;
}

export const ConnectStoreModal: React.FC<ConnectStoreModalProps> = ({
  open,
  onOpenChange,
  onStoreConnected,
}) => {
  const [selectedProvider, setSelectedProvider] = useState<'nuvemshop' | 'shopify' | 'woocommerce' | 'custom'>('nuvemshop');
  const [shopDomain, setShopDomain] = useState('');
  const [customStoreName, setCustomStoreName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleOAuthConnect = async (provider: 'shopify' | 'nuvemshop' | 'woocommerce') => {
    setIsLoading(true);
    try {
      const res = (await getApiOauthProviderAuthorize(provider)) as unknown as { authorizationUrl?: string; url?: string; provider?: string } | undefined;

      // If URL returned, open OAuth authorization flow
      const authUrl = res?.authorizationUrl || res?.url;
      if (authUrl) {
        toast.info(`Redirecionando para autorização OAuth do ${provider.toUpperCase()}...`);
        // We open OAuth or redirect
        window.location.href = authUrl;
      } else {
        toast.success(`Conectado ao ${provider} com sucesso!`);
        onOpenChange(false);
        onStoreConnected?.();
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Falha ao iniciar a autorização OAuth';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleManualConnect = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customStoreName.trim()) {
      toast.error('O nome da loja é obrigatório');
      return;
    }
    toast.success(`Loja "${customStoreName}" conectada com sucesso!`);
    setCustomStoreName('');
    onOpenChange(false);
    onStoreConnected?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] p-6">
        <DialogHeader className="space-y-1.5 pb-2">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Store className="h-4 w-4" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold">Conectar Loja Virtual</DialogTitle>
              <DialogDescription className="text-xs">
                Selecione sua plataforma de e-commerce para conectar o Agente de IA da sua loja
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <Tabs
          value={selectedProvider}
          onValueChange={(val) => setSelectedProvider(val as any)}
          className="w-full mt-2"
        >
          <TabsList className="grid grid-cols-4 w-full h-11 p-1">
            <TabsTrigger value="nuvemshop" className="text-xs gap-1 font-medium">
              <span>Nuvemshop</span>
            </TabsTrigger>
            <TabsTrigger
              value="shopify"
              disabled
              className="text-xs gap-1 opacity-50 cursor-not-allowed select-none"
            >
              <span>Shopify</span>
              <span className="text-[9px] font-normal text-muted-foreground/80 bg-muted-foreground/10 px-1 py-0.5 rounded leading-none">
                Em breve
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="woocommerce"
              disabled
              className="text-xs gap-1 opacity-50 cursor-not-allowed select-none"
            >
              <span>WooCommerce</span>
              <span className="text-[9px] font-normal text-muted-foreground/80 bg-muted-foreground/10 px-1 py-0.5 rounded leading-none">
                Em breve
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="custom"
              disabled
              className="text-xs gap-1 opacity-50 cursor-not-allowed select-none"
            >
              <span>Outra Plataforma</span>
              <span className="text-[9px] font-normal text-muted-foreground/80 bg-muted-foreground/10 px-1 py-0.5 rounded leading-none">
                Em breve
              </span>
            </TabsTrigger>
          </TabsList>

          {/* Nuvemshop Tab */}
          <TabsContent value="nuvemshop" className="space-y-4 pt-3">
            <div className="rounded-xl border border-blue-500/20 bg-blue-500/5 p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-foreground">Nuvemshop / Tiendanube</span>
                    <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 text-[10px]">
                      Conexão Oficial
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Integração oficial para conectar pedidos, produtos e clientes ao Agente de IA da sua loja.
                  </p>
                </div>
              </div>

              <div className="pt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-blue-500" />
                  Atendimento automático 24/7
                </span>
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-blue-500" />
                  Agente de IA pronto para responder na hora
                </span>
              </div>
            </div>

            <Button
              className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium"
              onClick={() => handleOAuthConnect('nuvemshop')}
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : <ExternalLink className="h-4 w-4" />}
              <span>Conectar com Nuvemshop</span>
            </Button>
          </TabsContent>

          {/* Shopify Tab */}
          <TabsContent value="shopify" className="space-y-4 pt-3">
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-foreground">Shopify</span>
                    <Badge className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20 text-[10px]">
                      Conexão Oficial
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Conexão em 1 clique para sincronizar pedidos, produtos e clientes com o Agente de IA.
                  </p>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-foreground">Domínio da Loja (Opcional)</Label>
                <div className="flex items-center">
                  <Input
                    placeholder="sua-loja"
                    value={shopDomain}
                    onChange={(e) => setShopDomain(e.target.value)}
                    className="h-9 text-xs font-mono rounded-r-none"
                  />
                  <span className="inline-flex items-center px-3 h-9 text-xs text-muted-foreground bg-muted border border-l-0 border-input rounded-r-md">
                    .myshopify.com
                  </span>
                </div>
              </div>

              <div className="pt-1 flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  Sincronização automática em tempo real
                </span>
                <span className="flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-emerald-500" />
                  Agente de IA pronto para responder na hora
                </span>
              </div>
            </div>

            <Button
              className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              onClick={() => handleOAuthConnect('shopify')}
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : <ExternalLink className="h-4 w-4" />}
              <span>Conectar com Shopify</span>
            </Button>
          </TabsContent>

          {/* WooCommerce Tab */}
          <TabsContent value="woocommerce" className="space-y-4 pt-3">
            <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4 space-y-3">
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-foreground">WooCommerce</span>
                  <Badge className="bg-purple-500/10 text-purple-600 dark:text-purple-400 border-purple-500/20 text-[10px]">
                    WordPress
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Conecte sua loja WooCommerce com rapidez e segurança.
                </p>
              </div>
            </div>

            <Button
              className="w-full gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium"
              onClick={() => handleOAuthConnect('woocommerce')}
              disabled={isLoading}
            >
              {isLoading ? <LoadingSpinner size="sm" /> : <ArrowRight className="h-4 w-4" />}
              <span>Conectar Loja WooCommerce</span>
            </Button>
          </TabsContent>

          {/* Custom API Tab */}
          <TabsContent value="custom" className="space-y-4 pt-3">
            <form onSubmit={handleManualConnect} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">Nome da Loja / Marca</Label>
                <Input
                  placeholder="ex. Minha Loja Personalizada"
                  value={customStoreName}
                  onChange={(e) => setCustomStoreName(e.target.value)}
                  className="h-9 text-xs"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold">URL do Site da Loja</Label>
                <Input
                  type="url"
                  placeholder="https://minhaloja.exemplo.com"
                  className="h-9 text-xs"
                />
              </div>

              <DialogFooter className="pt-2">
                <Button type="submit" size="sm" className="w-full gap-1.5">
                  <Store className="h-4 w-4" />
                  <span>Conectar Loja</span>
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
